import React, { useEffect, useMemo, useState } from "react";
import Layout from "@components/Layout";
import { API_BASE_URL } from "@lib/config";
import { type ChipTone } from "@components/cockpit";
import { useUser } from "../hooks/useUser";
// ONE derivation of harvest-lockout capital, shared with Risk and Signals so the three
// surfaces cannot quote different withheld amounts. See lib/harvestLockout.ts.
import {
  deriveHarvestLockout,
  type HarvestLockout,
  type HarvestPayloadLike,
} from "@lib/harvestLockout";
// The trimmable-lot minimum (CIO, 2026-09-17): a new line below it can never be trimmed at 2x.
// The server enforces it in create_order_ticket; this is the row-level preview.
import {
  applyLotRefusal,
  isLotExceptionRefusal,
  previewScreenerBuy,
  readLotMinimumRule,
  type LotRefusal,
  type ScreenerLotPreview,
} from "@lib/lotMinimum";
// The running deployment budget (CIO, 2026-09-17). The sizing box used to be read and
// discarded — the allocator never runs from this page. It now tracks what the CIO has chosen
// against the number he typed. It ranks nothing, sizes nothing, and blocks nothing.
import { computeScreenerBudget } from "@lib/screenerBudget";
// Screener Phase 1 correctness fixes (audit 2026-10-27): every fixed number, state and word is derived in this
// pure, unit-tested module. See docs/AUDIT_SCREENER_PAGE_2026-10-27.md.
import {
  BLANK_CONFIG_FORM,
  CONTRACTS_HEADERS,
  FULL_UNIVERSE_HEADERS,
  REQUESTER_MISSING_BLOCKER,
  buildConfigOverrideFields,
  buildRegimeRequestFields,
  buildTicketReasonText,
  contractsCells,
  currency,
  defenseOverlayDisplay,
  describeTenorShadow,
  diagnosticsHeadline,
  diagnosticsIssues,
  navAsOfPart,
  strikesByExpiry,
  vixAsOfPart,
  escHtml,
  fullUniverseCells,
  gradeWithTenorNote,
  htmlTable,
  mergeTicketStates,
  mlWeightFromInputs,
  numberFmt,
  parsePendingBuys,
  pctFmt,
  portfolioVegaFromRows,
  priorityToggleLabel,
  readTenorShadow,
  reportedOr,
  requesterIdentity,
  resolveMinDistinctExpiries,
  resolvedConfigValues,
  runUsedContext,
  shouldHydrateQueued,
  ticketContextTags,
  titleizeBucket,
  titleizeCode,
  type ConfigOverrideForm,
  type ContextLoadStatus,
} from "@lib/screenerTruth";
// Screener Phase 3 (2026-10-28): types, pure helpers and shared styles moved verbatim out of this file.
import type {
  ChainAudit,
  DataConfidence,
  DecisionContext,
  FilterAudit,
  Purchase,
  ScreenerRequest,
  ScreenerResponse,
  ScreenerRiskContext,
  SortDir,
  SortKey,
  TicketState,
  TilesMeta,
  Tone,
  UniverseGrade,
} from "@lib/screenerTypes";
import {
  allocationViolationDetail,
  allocationViolationTitle,
  buildOiCapsOverride,
  downloadExcelWorkbook,
  buildRiskContext,
  firstNumber,
  firstString,
  getCellValue,
  isRankedCandidate,
  isSelectedCandidate,
  localUniverseGrade,
  titleizeSkipReason,
  toneFromTenorBucket,
  topCountEntries,
  topCountText,
} from "@lib/screenerViewHelpers";
import {
  ui,
} from "@components/screener/screenerUi";
import NextBuySection from "@components/screener/NextBuySection";
import RankedContractsTable from "@components/screener/RankedContractsTable";
import ContextCard from "@components/screener/ContextCard";
import DiagnosticsDrawer from "@components/screener/DiagnosticsDrawer";
import RunBar from "@components/screener/RunBar";
import ScreenerFilters from "@components/screener/ScreenerFilters";

// Column counts live with the table they count (Screener Phase 3); re-exported for the page tests.
export { SCREENER_ANALYTICS_COLUMNS, SCREENER_BASE_COLUMNS } from "@components/screener/RankedContractsTable";


export default function ScreenerView() {
  const { userEmail } = useUser();
  // Phase 1 (finding 8): the real signed-in identity or null -- never the old "cio@helion" placeholder.
  const requester = requesterIdentity(userEmail);
  const [symbol, setSymbol] = useState("SPX");
  const [capital, setCapital] = useState<string>("");
  const [currentVega, setCurrentVega] = useState<string>("");
  // "Vega Cap (allocation sandbox only)" cut 2026-10-28 (Screener Phase 2, audit D6): it was never sent.

  // CIO decision 2026-09-10 (screener liquidity-gate audit, Step 1). These three
  // used to carry hard-coded defaults ("600", "50", and the four OI caps below) that
  // were sent on EVERY request and silently overrode HelionBackend/entry_signals.json
  // -- so the config file was not a description of live behaviour. They now start
  // BLANK, which sends null and lets the backend config decide; the placeholder shows
  // the value the backend actually resolved on the last run. Typing a value still
  // overrides, per run, for a sandbox screen -- that is a deliberate CIO action now
  // rather than an invisible default.
  const [minDte, setMinDte] = useState<string>("");
  const [earliestMonthsAhead, setEarliestMonthsAhead] = useState<string>("");
  // Phase 1 (audit 2026-10-27, finding 5): the remaining config-backed knobs start BLANK too and send null, so
  // entry_signals.json / env decide. The placeholders show the value the backend used on the last run
  // (meta.inputs), or "config default" before one. Typing a value still overrides, for this run only.
  const [bandPct, setBandPct] = useState<string>(BLANK_CONFIG_FORM.bandPct);
  const [moneynessBufferPct, setMoneynessBufferPct] = useState<string>(BLANK_CONFIG_FORM.moneynessBufferPct);
  const [minOI, setMinOI] = useState<string>("");
  const [monthsLimit, setMonthsLimit] = useState<string>("0");
  const [right, setRight] = useState<"C" | "P">("C");

  // Max Tranches x2 cut 2026-10-28 (Phase 2, audit D9): allocator-only, inert in market-rank mode. Blank = omitted.
  // Blank = use HelionBackend/entry_signals.json oi_caps. See the note on minDte above.
  const [oiCapUnder100, setOiCapUnder100] = useState<string>("");
  const [oiCap100to499, setOiCap100to499] = useState<string>("");
  const [oiCap500to1499, setOiCap500to1499] = useState<string>("");
  const [oiCap1500plus, setOiCap1500plus] = useState<string>("");

  const [expirationCapPct, setExpirationCapPct] = useState<string>(BLANK_CONFIG_FORM.expirationCapPct);
  const [minDistinctExpiries, setMinDistinctExpiries] = useState<string>(BLANK_CONFIG_FORM.minDistinctExpiries);
  // CUT 2026-10-28 (Screener Phase 2, audit D9, CIO-approved): Per-Expiry Vega Cap, Line Share Cap, Min Qty per New
  // Line, Block Qty, Filler Mode / Enabled, Final Top-up / Max Units (and Max Tranches above). Allocator-only, so
  // inert in market-rank mode; Phase 1 had already made them start blank and send null. They are no longer
  // rendered and always take BLANK_CONFIG_FORM (null / omitted) -- the request body of a run is unchanged, pinned
  // by ScreenView.phase2.test.tsx. Per-Expiry Notional Cap and Min Distinct Expiries stay: the first feeds the
  // ladder's "full" test, the second the breadth target.
  const [showFilters, setShowFilters] = useState(false);
  // Diagnostics drawer: null = follow the run's state (opens itself on any non-pass); true/false = the CIO's toggle.
  const [diagUserOpen, setDiagUserOpen] = useState<boolean | null>(null);
  const [showAnalyticsColumns, setShowAnalyticsColumns] = useState(false);
  // showML removed 2026-08-11 with the Options ML overlay card.
  const [expandedContractKey, setExpandedContractKey] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ScreenerResponse | null>(null);
  const [stageBanner, setStageBanner] = useState<{ tone: Tone; text: string } | null>(null);
  const [stagingAll, setStagingAll] = useState(false);
  // Contracts the CIO has ticked to send to the Trading Desk. Keyed on conid as a
  // string, matching ticketStateByConid so the two never disagree about identity.
  const [selectedForQueue, setSelectedForQueue] = useState<Set<string>>(new Set());
  // Quantities the CIO typed for contracts the allocator did NOT size. The screener
  // is informational — it shows how contracts size up against each other; the CIO
  // decides what to buy and how much. A row the allocator skipped or merely ranked
  // is still a contract the CIO may want, so it needs somewhere to put a size.
  const [cioQtyByConid, setCioQtyByConid] = useState<Record<string, string>>({});
  // Exception reasons for rows below the trimmable-lot minimum, keyed like cioQtyByConid.
  const [lotExceptionByConid, setLotExceptionByConid] = useState<Record<string, string>>({});
  // Rows the SERVER refused for the lot minimum, with the quantity it judged. The server outranks
  // the preview (whose held book was read at page load); without this a stale "top-up" preview
  // would dead-end: refused, no reason box, no way to queue. See applyLotRefusal.
  const [lotRefusedByConid, setLotRefusedByConid] = useState<Record<string, LotRefusal>>({});
  const [stagingConid, setStagingConid] = useState<string | null>(null);
  const [ticketStateByConid, setTicketStateByConid] = useState<Record<string, TicketState>>({});

  // What THIS SESSION has actually sent to the Trading Desk, at the size it was sent.
  //
  // Deliberately a separate map from ticketStateByConid, which (a) carries no quantity and
  // (b) is wiped on every Run Screener. Capital committed to a queued ticket is still
  // committed after a re-run, so a tally sourced from that map would reset to zero and report
  // more budget left than there is. Only a ticket THIS page staged is recorded here:
  // hydrated "queued" rows may be days old, and a 409 duplicate is not new spend.
  const [sessionQueuedByConid, setSessionQueuedByConid] = useState<
    Record<string, { qty: number; notional: number | null }>
  >({});

  const [tilesMeta, setTilesMeta] = useState<TilesMeta>(null);
  // Phase 1 (finding 4): a missing vega is not $0 -- the sum carries how many lines had none.
  const [portfolioVega, setPortfolioVega] = useState<{ total: number | null; lines: number; missingVega: number; asOf: string | null } | null>(null);
  const [riskContext, setRiskContext] = useState<ScreenerRiskContext | null>(null);
  // Phase 1 (findings 2 + 4): Run waits for the risk context, and a failed load is SAID, never silent.
  const [riskContextStatus, setRiskContextStatus] = useState<ContextLoadStatus>("loading");
  const [contextErrors, setContextErrors] = useState<{ snapshot?: string; kpis?: string; surface?: string; vega?: string }>({});
  // Phase 1 (finding 3): a failed pending-ticket re-read keeps the last known state and says so.
  const [pendingReadFailed, setPendingReadFailed] = useState<string | null>(null);

  // HARVEST LOCKOUT. The screener's sizing budget is a number the CIO TYPES, and nothing on
  // this page ever told him that some of the fund's capital is under a redeployment lockout —
  // so he could size a full deployment straight through a live lock. /home_run_harvest is the
  // purpose-built endpoint for it (the same one the Signals harvest panel reads); risk/kpis,
  // which this page already calls, carries no lock fields at all. Read-only and additive: a
  // failure here degrades the disclosure to "unknown" and blocks nothing.
  const [harvestPayload, setHarvestPayload] = useState<HarvestPayloadLike>(undefined);
  const [harvestFetchedAtMs, setHarvestFetchedAtMs] = useState<number | null>(null);
  const [nowTickMs, setNowTickMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowTickMs(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/home_run_harvest`);
        if (!res.ok) throw new Error(`home_run_harvest HTTP ${res.status}`);
        const payload = await res.json();
        if (cancelled) return;
        setHarvestPayload(payload && typeof payload === "object" ? payload : null);
        setHarvestFetchedAtMs(Date.now());
      } catch {
        if (cancelled) return;
        // null => the shared module reports UNKNOWN. It must never fall back to "$0 locked".
        setHarvestPayload(null);
        setHarvestFetchedAtMs(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  // Null when home_run_harvest did not load or predates the block: the preview then blocks
  // nothing and create_order_ticket remains the gate.
  const lotRule = useMemo(() => readLotMinimumRule(harvestPayload), [harvestPayload]);
  const harvestLockout = useMemo<HarvestLockout>(
    () =>
      deriveHarvestLockout({
        harvest: harvestPayload,
        fetchedAtMs: harvestFetchedAtMs,
        nowMs: nowTickMs,
      }),
    [harvestPayload, harvestFetchedAtMs, nowTickMs]
  );

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/get_leaps_snapshot`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const payload = await res.json();
        setTilesMeta({
          vixZScore: payload?.meta?.vixZScore,
          regime: payload?.meta?.regime,
        });
      } catch (err: any) {
        // Phase 1 (finding 4): no longer silent -- the card says the context did not load.
        setContextErrors((prev) => ({ ...prev, snapshot: `get_leaps_snapshot ${err?.message || "failed"}` }));
      }
    })();

    (async () => {
      try {
        const [kpiRes, surfaceRes] = await Promise.all([
          fetch(`${API_BASE_URL}/risk/kpis?axis_mode=vix`),
          fetch(`${API_BASE_URL}/risk/surface_overview`).catch(() => null as any),
        ]);

        const kpiPayload = kpiRes && kpiRes.ok ? await kpiRes.json() : null;
        const surfacePayload = surfaceRes && surfaceRes.ok ? await surfaceRes.json() : null;
        if (!kpiPayload) {
          setContextErrors((prev) => ({ ...prev, kpis: `risk/kpis ${kpiRes ? `HTTP ${kpiRes.status}` : "failed"}` }));
        }
        if (!surfacePayload) {
          setContextErrors((prev) => ({ ...prev, surface: `risk/surface_overview ${surfaceRes ? `HTTP ${surfaceRes.status}` : "failed"}` }));
        }

        setRiskContext(buildRiskContext(kpiPayload, surfacePayload, tilesMeta));
        setRiskContextStatus(kpiPayload ? "loaded" : "failed");
      } catch (err: any) {
        // Phase 1 (finding 4): a failed context load is visible, and Run is released with a stated default.
        setContextErrors((prev) => ({ ...prev, kpis: `risk context ${err?.message || "failed"}` }));
        setRiskContextStatus("failed");
      }
    })();

    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/risk/leaps_metrics`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const payload = await res.json();
        const rows = Array.isArray(payload) ? payload : payload?.rows || [];
        // Phase 1 (finding 4): a missing vega is counted, not added as $0; no rows / all missing -> "—".
        const v = portfolioVegaFromRows(rows);
        setPortfolioVega({ ...v, asOf: firstString(payload?.asOfUTC, payload?.asOf) });
        if (v.total != null) setCurrentVega(v.total.toFixed(1));
      } catch (err: any) {
        setPortfolioVega(null);
        setContextErrors((prev) => ({ ...prev, vega: `risk/leaps_metrics ${err?.message || "failed"}` }));
      }
    })();
  }, []);

  // The cut allocator knobs always take their BLANK value (null / omitted); see the note on the state above.
  const configForm: ConfigOverrideForm = useMemo(
    () => ({
      ...BLANK_CONFIG_FORM,
      bandPct,
      moneynessBufferPct,
      expirationCapPct,
      minDistinctExpiries,
    }),
    [bandPct, moneynessBufferPct, expirationCapPct, minDistinctExpiries]
  );

  const body: ScreenerRequest = useMemo(() => {
    return {
      symbol: symbol.trim().toUpperCase(),
      capital_to_deploy: capital.trim() !== "" && !isNaN(Number(capital)) ? Number(capital) : null,
      current_vega_usd_per_pt: currentVega.trim() !== "" && !isNaN(Number(currentVega)) ? Number(currentVega) : null,
      vega_nav_cap_pct: null,
      market_rank_only: true,
      include_allocation: false,
      include_all_expiries: monthsLimit === "" || Number(monthsLimit) <= 0,
      include_filter_audit: true,
      include_chain_audit: true,
      include_universe_audit: true,
      max_audit_rows: 1000,
      quote_hydration_enabled: true,
      min_dte: minDte !== "" ? Number(minDte) : null,
      min_open_interest: minOI !== "" ? Number(minOI) : null,
      allow_missing_oi_for_rank: true,
      months_limit: monthsLimit !== "" ? Number(monthsLimit) : null,
      right,
      // Phase 1 (finding 5): moneyness band/buffer, the governance/filler knobs and quote_hydration_* are null
      // unless typed (max_tranches_* are omitted when blank -- see buildConfigOverrideFields).
      ...buildConfigOverrideFields(configForm),
      // Send ONLY the tiers the CIO actually typed. The backend merges a partial dict
      // over entry_signals.json, so an omitted tier keeps its configured value and an
      // entirely blank panel sends null -- config wins, which is the Step 1 intent.
      // Previously this object was always fully populated from hard-coded UI defaults,
      // which is how oi_caps.under_100 ran at 0.05 while the config file said 0.0.
      oi_caps: buildOiCapsOverride({
        under_100: oiCapUnder100,
        "100_to_499": oiCap100to499,
        "500_to_1499": oiCap500to1499,
        "1500_or_more": oiCap1500plus,
      }),
      earliest_months_ahead:
        earliestMonthsAhead !== "" ? Number(earliestMonthsAhead) : null,
      // Phase 1 (CIO decision 2): deployment_mode is null -- the backend's shared resolver
      // (entry_ranking.resolve_deployment_mode) decides from the raw regime fields sent here.
      ...buildRegimeRequestFields(riskContext, riskContextStatus),
      effective_target_leaps_pct: null,
      current_leaps_allocation_pct: null,
      current_leaps_basis_allocation_pct: null,
      effective_target_leaps_basis_pct: null,
      portfolio_nav: null,
      suggested_vega_cap_usd_per_pt: null,
      suggested_vega_cap_bps_low: null,
      suggested_vega_cap_bps_high: null,
      max_capital_to_nav_pct: null,
      allow_capital_above_nav: true,
    };
  }, [
    configForm,
    riskContextStatus,
    riskContext,
    symbol,
    capital,
    currentVega,
    minDte,
    earliestMonthsAhead,
    minOI,
    monthsLimit,
    right,
    oiCapUnder100,
    oiCap100to499,
    oiCap500to1499,
    oiCap1500plus,
  ]);

  const capitalNum = capital.trim() === "" ? null : Number(capital);
  // Phase 1 (finding 2 / CIO decision 2): Run waits until the risk context has loaded OR visibly failed, so a fast
  // click can no longer rank in a silent default mode. On failure Run is released and the page says so.
  const canSubmit =
    !loading &&
    riskContextStatus !== "loading" &&
    Boolean(symbol.trim()) &&
    (capitalNum == null || (Number.isFinite(capitalNum) && capitalNum >= 0));

  // Phase 1 (finding 3): re-read pending BUY tickets whenever the run produced candidate rows. The old gate was
  // data.purchases -- filled only by the allocator, so ALWAYS empty in market-rank mode -- and the read never ran:
  // a contract queued yesterday showed as open to buy. The server list replaces hydrated entries; tickets this
  // session staged are kept (mergeTicketStates). A failed read keeps the last known state and says so.
  const candidateRowCount =
    data?.rankedContracts?.length || data?.purchases?.length || 0;
  useEffect(() => {
    let cancelled = false;

    async function hydrateQueuedBuys() {
      if (!shouldHydrateQueued(candidateRowCount)) return;

      try {
        const res = await fetch(`${API_BASE_URL}/get_pending_orders`, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const payload = await res.json();
        const server = parsePendingBuys(payload);
        if (!cancelled) {
          setTicketStateByConid((prev) => mergeTicketStates(server, prev));
          setPendingReadFailed(null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setPendingReadFailed(
            `Pending tickets could not be re-read (${err?.message || "error"}); queued markers may be out of date. The Trading Desk still refuses a duplicate.`
          );
        }
      }
    }

    hydrateQueuedBuys();
    return () => {
      cancelled = true;
    };
  }, [data?.meta?.asOf, candidateRowCount]);

  async function runScreener(e?: React.FormEvent, overrides?: Partial<ScreenerRequest>) {
    e?.preventDefault();
    setError(null);
    setStageBanner(null);
    // Ticket state is NOT wiped on Run (Phase 1, finding 3): the pending list is re-read when the new run lands.
    setExpandedContractKey(null);
    setLoading(true);
    setData(null);

    const corrId =
      (typeof crypto !== "undefined" &&
        "randomUUID" in crypto &&
        crypto.randomUUID()) ||
      Math.random().toString(36).slice(2);

    try {
      const url = `${API_BASE_URL}/screener`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-corr-id": corrId },
        body: JSON.stringify({ ...body, ...(overrides || {}) }),
      });

      if (!res.ok) {
        const text = await res.text();
        try {
          const j = JSON.parse(text);
          const id = j["x-corr-id"] || res.headers.get("x-corr-id") || corrId;
          throw new Error(`${j.error || text} (corr-id: ${id})`);
        } catch {
          const id = res.headers.get("x-corr-id") || corrId;
          throw new Error(`${text || `HTTP ${res.status}`} (corr-id: ${id})`);
        }
      }

      const payload = (await res.json()) as ScreenerResponse;
      setData(payload);
    } catch (err: any) {
      setError(err?.message || "Request failed");
    } finally {
      setLoading(false);
    }
  }

  /** Standard US index-option multiplier. Mirrors the backend's OPTION_CONTRACT_MULTIPLIER. */
  const CONTRACT_MULTIPLIER = 100;

  /**
   * The size to stage, and who decided it.
   *
   * The allocator sizes the contracts it selects. For anything else — a ranked row,
   * or one the allocator skipped — the CIO supplies the quantity, and the ticket is
   * marked CIO-originated so the ledger can later separate discretionary picks from
   * engine-sized ones.
   */
  function resolveTicketSizing(p: Purchase): {
    qty: number;
    notional: number | null;
    origin: "SCREENER" | "CIO";
    cioSized: boolean;
  } {
    if (isSelectedCandidate(p)) {
      return {
        qty: Number(p.allocatedQty || 0),
        notional: Number(p.estNotional || 0),
        origin: "SCREENER",
        cioSized: false,
      };
    }
    const qty = Number.parseInt(cioQtyByConid[String(p.conid)] || "", 10);
    const mid = Number(p.mid);
    // No fabricated notional: without a usable mid we cannot state the dollar size,
    // and the backend rejects a non-positive targetNotional rather than storing one.
    const notional =
      Number.isFinite(qty) && qty > 0 && Number.isFinite(mid) && mid > 0
        ? Number((qty * mid * CONTRACT_MULTIPLIER).toFixed(2))
        : null;
    return { qty: Number.isFinite(qty) ? qty : 0, notional, origin: "CIO", cioSized: true };
  }

  /**
   * The lot-minimum preview for this row at the size that would be staged, overridden by the
   * server's refusal for that same quantity when there is one.
   */
  function lotPreviewFor(p: Purchase): ScreenerLotPreview {
    const key = String(p.conid);
    const qty = resolveTicketSizing(p).qty;
    return applyLotRefusal(previewScreenerBuy(lotRule, key, qty), lotRefusedByConid[key], qty);
  }

  /** Can this row be queued at all right now, and if not, why not. */
  function queueBlocker(p: Purchase): string | null {
    if (ticketStateByConid[String(p.conid)]) return "Already queued";
    // Phase 1 (finding 8): never queue under a placeholder identity. The ticket's requestedBy and the
    // TradeAudit EntryUser must be a real signed-in user.
    if (!requester) return REQUESTER_MISSING_BLOCKER;
    if (!isSelectedCandidate(p)) {
      const { qty, notional } = resolveTicketSizing(p);
      if (!(qty > 0)) return "Enter a quantity to queue this contract";
      if (notional == null) return "No usable mid price — cannot compute the notional";
    }
    const lot = lotPreviewFor(p);
    if (lot.requiresException && !(lotExceptionByConid[String(p.conid)] || "").trim()) {
      return lot.minContracts != null
        ? `Below the ${lot.minContracts}-contract minimum — buy ${lot.minContracts}, or give an exception reason`
        : "Below the contract minimum — give an exception reason";
    }
    return null;
  }

  /**
   * A human-readable CONTRACT label, e.g. "SPX 2031-12-19 C8000".
   *
   * This is what gets staged into dbo.PendingOrders.Symbol, and from there onto the
   * ledger entry. Previously the bare underlying ("SPX") was staged, which on a
   * single-underlying book identifies nothing: the Trade Desk showed "SPX" for every
   * ticket, and a ledger entry a year from now would say only "SPX".
   *
   * The Trade Desk can normally resolve a proper name by joining dbo.v_HeldLeaps_Display,
   * but that view covers HELD positions only — so a NEW buy, which is exactly what the
   * Screener stages, never has a row to join to. The label has to travel with the ticket.
   *
   * Kept inside varchar(32): "SPX 2031-12-19 C8000" is 20 characters.
   */
  function contractLabelFor(p: Purchase): string {
    const underlying = String(data?.meta?.symbol ?? p.symbol ?? "").trim();
    const expiry = String(p.expiry ?? "").trim().slice(0, 10);
    const right = String(p.right ?? "").trim().toUpperCase().slice(0, 1);
    const hasStrike = Number.isFinite(Number(p.strike));

    // An instrument with no expiry or strike is an ETF / fixed-income / alts line.
    // For those the TICKER is the whole identity — there is nothing else to state.
    if (!expiry || !right || !hasStrike) return underlying.slice(0, 32);

    // Options get the standardised name: SPX 19DEC2031 C8000
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(expiry);
    const MON = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
    const exp = m && MON[Number(m[2]) - 1] ? `${m[3]}${MON[Number(m[2]) - 1]}${m[1]}` : expiry;
    const strike = Number(p.strike).toLocaleString(undefined, { maximumFractionDigits: 2 }).replace(/,/g, "");
    return `${underlying} ${exp} ${right}${strike}`.replace(/\s+/g, " ").trim().slice(0, 32);
  }

  function buildTicketReason(p: Purchase, cioSized = false): string {
    // Phase 1 (finding 8): in market-rank mode the allocator never ran, so the stored reason says
    // "allocator: not run (market rank)" -- it used to say "allocator: Selected" on every ticket.
    return buildTicketReasonText(p, { cioSized, marketRankOnly });
  }

  function buildTicketTags(p: Purchase, cioSized = false) {
    return {
      source: "screener",
      // Records WHO chose the size, so the ledger can later compare discretionary
      // picks against engine-sized ones rather than treating them as the same thing.
      sizedBy: cioSized ? "cio" : "allocator",
      allocatorStatus: String(p.allocationStatus || "").toUpperCase() || null,
      allocatorSkipReason: cioSized ? p.skipReason ?? null : null,
      symbol: data?.meta?.symbol ?? p.symbol,
      asOf: data?.meta?.asOf ?? new Date().toISOString(),
      conid: String(p.conid),
      expiry: p.expiry,
      right: p.right,
      strike: p.strike,
      dte: p.dte,
      tenorBucket: p.tenorBucket,
      surfaceRegime: p.surfaceRegime,
      surfaceValueLabel: p.surfaceValueLabel,
      surfaceShapeLabel: p.surfaceShapeLabel,
      entryScore: p.entryScore,
      surfaceScore: p.surfaceScore,
      iv: p.iv,
      moneyness: p.moneyness,
      estVegaUsdPerPt: p.estVegaUSDperPt,
      // The mode the RUN used (backend echo), not a page guess (CIO decision 2).
      deploymentMode: runUsed.mode,
      effectiveRegime: data?.meta?.inputs?.risk_context?.effective_regime ?? riskContext?.engineEffectiveRegime ?? null,
      // Phase 1 (finding 8): target and current on the SAME (funded cost) basis, market value recorded apart,
      // plus the Priority context the CIO acted on.
      ...ticketContextTags({
        basisTargetLeapsPct: riskContext?.basisTargetLeapsPct,
        targetLeapsPct: riskContext?.targetLeapsPct,
        basisLeapsAllocationPct: riskContext?.basisLeapsAllocationPct,
        currentLeapsAllocationPct: riskContext?.currentLeapsAllocationPct,
        priorityRank: p.priorityRank,
        rungStatus: p.rungStatus,
        ladderPriorityActive: data?.ladderPriority?.active ?? null,
      }),
      dataConfidenceStatus: dataConfidence?.status ?? null,
      decisionContextStatus: decisionContext?.status ?? null,
      cioAction: decisionContext?.cioAction ?? null,
      requestedBy: requester,
    };
  }

  async function stagePurchaseTicket(
    p: Purchase,
    options?: { suppressBanner?: boolean }
  ): Promise<"staged" | "duplicate"> {
    if (trustHardBlock) {
      throw new Error(
        `Screener staging is locked by a hard data or invariant guardrail. ${trustHardBlocker || "Review Data Confidence and Guardrail Status."}`
      );
    }

    // The screener is INFORMATIONAL — it shows how contracts size up against each
    // other. The CIO decides what to buy. So a ranked or skipped row is not
    // un-stageable; it simply has no engine-supplied size, and the CIO must give it
    // one. What we refuse is staging a size we cannot state honestly.
    const blocker = queueBlocker(p);
    if (blocker) throw new Error(blocker);

    const sizing = resolveTicketSizing(p);
    if (!(sizing.qty > 0) || sizing.notional == null) {
      throw new Error("A quantity and a usable mid price are required to queue this contract.");
    }

    const key = String(p.conid);
    // Sent only when typed. The box renders only for a row below the minimum, so a reason
    // present here is a deliberate exception; the server ignores it on a row that meets it.
    const lotException = (lotExceptionByConid[key] || "").trim();
    setStagingConid(key);

    try {
      const res = await fetch(`${API_BASE_URL}/create_order_ticket`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "BUY",
          contractId: key,
          // Full contract label, not the bare underlying — see contractLabelFor.
          symbol: contractLabelFor(p),
          estimatedQty: sizing.qty,
          targetNotional: sizing.notional,
          origin: sizing.origin,
          // CORRECTED 2026-08-13. These two fields were written from DIFFERENT bases --
          // targetLeapsPct is the funded/cost-basis deployment cap, currentLeapsAllocationPct
          // is MARKET value -- so every ticket archived a mismatched pair into the permanent
          // trade record. Both now come from the funded basis the cap actually governs, which
          // is the pair a later reader needs to judge whether the purchase was within policy.
          // Market value is recorded separately rather than dropped.
          targetAllocation: riskContext?.basisTargetLeapsPct ?? riskContext?.targetLeapsPct ?? undefined,
          currentAllocation: riskContext?.basisLeapsAllocationPct ?? undefined,
          currentAllocationMarketValue: riskContext?.currentLeapsAllocationPct ?? undefined,
          allocationBasis: "funded_cost",
          reason: buildTicketReason(p, sizing.cioSized),
          limitPrice: p.mid ?? undefined,
          // The row's own quote at screen time (2026-10-23). The backend also reads the live market when
          // the ticket is created and keeps both, so screen-to-ticket drift is visible. Measurement only.
          screenQuote: { bid: p.bid ?? null, ask: p.ask ?? null, asOf: data?.meta?.asOf ?? null },
          tags: buildTicketTags(p, sizing.cioSized),
          requestedBy: requester,
          scheduledDate: new Date().toISOString().slice(0, 10),
          ...(lotException ? { lotMinimumException: lotException } : {}),
        }),
      });

      const payload = await res.json().catch(() => ({}));

      if (res.ok) {
        const orderId = Number(payload?.orderId ?? NaN);
        // Record the committed size for the running budget line. Only here, on a 200 — a 409
        // duplicate means the ticket already existed and is not new deployment.
        setSessionQueuedByConid((prev) => ({
          ...prev,
          [key]: { qty: sizing.qty, notional: sizing.notional },
        }));
        setTicketStateByConid((prev) => ({
          ...prev,
          [key]: {
            status: "staged",
            orderId: Number.isFinite(orderId) ? orderId : null,
            label: Number.isFinite(orderId) ? `Queued #${orderId}` : "Queued",
          },
        }));

        if (!options?.suppressBanner) {
          setStageBanner({
            tone: "success",
            text:
              `Queued BUY ticket for ${p.symbol} ${p.expiry} ${p.right}${numberFmt(p.strike, 0)}. ` +
              `It is now waiting in Trading Desk for CIO approval before any IBKR staging.`,
          });
        }

        return "staged";
      }

      if (res.status === 409) {
        const existing = payload?.existing || {};
        const existingOrderId = Number(existing?.orderId ?? NaN);
        const existingStatus = String(existing?.status || "ALREADY_QUEUED").toUpperCase();
        setTicketStateByConid((prev) => ({
          ...prev,
          [key]: {
            status: "duplicate",
            orderId: Number.isFinite(existingOrderId) ? existingOrderId : null,
            label: Number.isFinite(existingOrderId)
              ? `${existingStatus} #${existingOrderId}`
              : existingStatus,
          },
        }));

        if (!options?.suppressBanner) {
          setStageBanner({
            tone: "info",
            text:
              `A live BUY ticket already exists for ${p.symbol} ${p.expiry} ${p.right}${numberFmt(p.strike, 0)}. ` +
              `No duplicate ticket was created.`,
          });
        }

        return "duplicate";
      }

      if (res.status === 422 && isLotExceptionRefusal(payload)) {
        // The server's lot-minimum backstop. Keep the row ticked and open its reason box.
        setLotRefusedByConid((prev) => ({ ...prev, [key]: { qty: sizing.qty, message: String(payload?.error || "") } }));
        throw new Error(
          `${String(payload?.error || "Below the contract minimum.")} Type an exception reason on the row to queue it anyway.`
        );
      }

      // Surface the SERVER'S DIAGNOSTIC, not just its headline.
      //
      // create_order_ticket returns {error, details} where details is the actual
      // driver message (e.g. the SQL error text). This threw away `details`, so a
      // failed stage read "Database error while creating order ticket." and nothing
      // else — enough to know it broke, useless for knowing why, and the detail was
      // sitting in the response the whole time.
      throw new Error(
        [payload?.error || `HTTP ${res.status}`, payload?.details]
          .filter(Boolean)
          .join(" — ")
      );
    } finally {
      setStagingConid((current) => (current === key ? null : current));
    }
  }

  async function stageSinglePurchase(p: Purchase) {
    setError(null);
    setStageBanner(null);
    try {
      await stagePurchaseTicket(p);
    } catch (err: any) {
      setStageBanner({ tone: "danger", text: err?.message || "Failed to queue BUY ticket." });
    }
  }

  /**
   * Queue only the contracts the CIO ticked.
   *
   * Previously the only choices were one row at a time or "Queue All Buys" — take
   * the allocator's entire basket or none of it. Putting capital to work usually
   * means wanting SOME of what the screen surfaced, so selection is the missing
   * middle. Everything else is unchanged: the same guardrails, the same
   * create_order_ticket path, the same manual approval on the Trading Desk.
   */
  async function stageSelectedPurchases() {
    if (trustHardBlock) {
      setStageBanner({
        tone: "danger",
        text: `Screener staging is locked by a hard data or invariant guardrail. ${trustHardBlocker || "Review Data Confidence and Guardrail Status."}`,
      });
      return;
    }

    const chosen = readyToQueue;
    if (!chosen.length) {
      // Distinguish "nothing ticked" from "ticked but not yet sized" — they need
      // different actions from the CIO.
      const tickedButBlocked = allCandidateRows.filter(
        (p) => selectedForQueue.has(String(p.conid)) && queueBlocker(p) != null
      );
      setStageBanner({
        tone: "info",
        text: tickedButBlocked.length
          ? `${tickedButBlocked.length} selected contract${tickedButBlocked.length === 1 ? " is" : "s are"} not ready — ${tickedButBlocked[0] ? queueBlocker(tickedButBlocked[0]) : ""}.`
          : "Nothing selected to queue.",
      });
      return;
    }

    const cioCount = chosen.filter((p) => !isSelectedCandidate(p)).length;
    const confirmed = window.confirm(
      `Queue ${chosen.length} BUY ticket${chosen.length === 1 ? "" : "s"} to the Trading Desk?\n\n` +
        chosen
          .map((p) => {
            const s = resolveTicketSizing(p);
            return `  • ${p.expiry} ${p.right}${numberFmt(p.strike, 0)} — qty ${s.qty}${s.cioSized ? " (your size)" : ""}`;
          })
          .join("\n") +
        (cioCount
          ? `\n\n${cioCount} of these ${cioCount === 1 ? "was" : "were"} sized by you, not the allocator, and will be recorded that way.`
          : "") +
        `\n\nThis stages tickets only. Nothing is sent to IBKR from the screener.`
    );
    if (!confirmed) return;

    setError(null);
    setStageBanner(null);
    setStagingAll(true);

    let staged = 0;
    let duplicates = 0;
    const failed: string[] = [];
    const done: string[] = [];

    try {
      for (const purchase of chosen) {
        try {
          const outcome = await stagePurchaseTicket(purchase, { suppressBanner: true });
          if (outcome === "staged") staged += 1;
          else duplicates += 1;
          done.push(String(purchase.conid));
        } catch (err: any) {
          failed.push(`${purchase.expiry} ${purchase.right}${numberFmt(purchase.strike, 0)}: ${err?.message || "error"}`);
        }
      }

      // Only clear what actually queued. A failed row stays ticked so it is not
      // silently dropped from the intent.
      setSelectedForQueue((prev) => {
        const next = new Set(prev);
        done.forEach((k) => next.delete(k));
        return next;
      });

      const tone: Tone = failed.length ? "warning" : staged > 0 ? "success" : "info";
      setStageBanner({
        tone,
        text: [
          staged ? `${staged} queued to the Trading Desk` : null,
          duplicates ? `${duplicates} already live` : null,
          failed.length ? `${failed.length} failed — ${failed.join("; ")}` : null,
        ]
          .filter(Boolean)
          .join(" · "),
      });
    } finally {
      setStagingAll(false);
    }
  }

  // stageAllPurchases ("Queue All Buys") removed 2026-10-28, Screener Phase 2: allocator-only, always disabled in
  // market-rank mode. Queue Selected (below the ladder) is the one queue path.

  // Default = PRIORITY order (CIO 2026-10-22): tenor priority first, then Entry Score -- the
  // order the allocator walks and the CIO's buys have followed. Entry Score alone puts the
  // DEC2030/31 boards on top, and live IBKR quotes those 8.7-15% of mid wide vs 0.3-0.5% on the
  // 2028 boards (docs/SCREENER_LIQUIDITY_GATES_AUDIT.md §12). Entry Score is one click away.
  const [sortKey, setSortKey] = useState<SortKey>("priorityRank");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  function toggleSort(key: SortKey) {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "entryScore" || key === "surfaceScore" ? "desc" : "asc");
    }
  }

  const sweetSpotNotional = useMemo(() => {
    if (!data?.purchases) return 0;
    return data.purchases
      .filter((p) => p.tenorBucket === "24_30m")
      .reduce((acc, p) => acc + (p.estNotional || 0), 0);
  }, [data?.purchases]);

  const activeAllocationBucket = useMemo(() => {
  if (!data?.purchases?.length) return null;

  const counts = data.purchases.reduce<Record<string, number>>((acc, p) => {
    const key = p.tenorBucket || "unbucketed";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const top = (Object.entries(counts) as [string, number][])
    .sort((a, b) => b[1] - a[1])[0];

  return top ? top[0] : null;
  }, [data?.purchases]);

  const diagnostics = data?.meta?.diagnostics;
  const dataConfidence: DataConfidence | null = data?.dataConfidence || data?.meta?.data_confidence || null;
  const decisionContext: DecisionContext | null =
    data?.decisionContext || data?.trustSummary || data?.marketScreenerSummary || data?.meta?.decision_context || data?.meta?.trust_summary || null;
  const hardDecisionBlocks = Array.isArray(decisionContext?.hardBlocks) ? decisionContext?.hardBlocks || [] : [];
  const dataConfidenceFailed = String(dataConfidence?.status || decisionContext?.dataConfidence?.status || "").toLowerCase() === "fail";
  const decisionContextFailed = String(decisionContext?.status || "").toLowerCase() === "fail";
  const trustDecisionBlocker = [
    dataConfidenceFailed ? "Data confidence failed" : null,
    decisionContextFailed ? "Decision context returned FAIL" : null,
    ...(hardDecisionBlocks || []).map(titleizeCode),
  ]
    .filter(Boolean)
    .join(" | ");
  const allocationValidation = diagnostics?.allocation_validation ?? null;
  const allocationValidationViolations = Array.isArray(allocationValidation?.violations)
    ? allocationValidation!.violations
    : [];
  const allocationValidationFailed = Boolean(
    allocationValidation &&
      (String(allocationValidation.status || "").toLowerCase() === "fail" ||
        allocationValidationViolations.length > 0)
  );
  const allocationValidationBlocker = allocationValidationFailed
    ? allocationValidationViolations.length
      ? allocationValidationViolations
          .map(
            (violation, idx) =>
              `${idx + 1}. ${allocationViolationTitle(violation.code)} — ${allocationViolationDetail(violation)}`
          )
          .join(" | ")
      : "Backend allocation validation returned FAIL without a detailed violation payload."
    : null;
  const trustHardBlock = Boolean(allocationValidationFailed || dataConfidenceFailed || decisionContextFailed || hardDecisionBlocks.length > 0);
  const trustHardBlocker = [allocationValidationBlocker, trustDecisionBlocker].filter(Boolean).join(" | ") || null;
  const marketRankOnly = Boolean(data?.totals?.market_rank_only || data?.meta?.inputs?.market_rank_only || decisionContext?.allocatorResult?.code === "ranked_contracts");
  // Phase 1 (finding 4): "Unknown", not "Inactive", when the risk feed failed or answered from its fallback.
  const defenseDisplay = defenseOverlayDisplay({
    status: riskContextStatus,
    regimeContextSource: riskContext?.regimeContextSource,
    defenseOverlayActive: riskContext?.defenseOverlayActive,
    defenseOverlayState: riskContext?.defenseOverlayState,
  });
  // Phase 1 (CIO decision 2): what the RUN used, from the backend's echo -- never the page's guess.
  const runUsed = runUsedContext(data?.meta?.inputs?.risk_context, defenseDisplay.known);
  // Phase 1 (finding 10): the ML weight the run used; at 0 an ML failure changes no score.
  const mlWeight = mlWeightFromInputs(data?.meta?.inputs);
  // Phase 1 (finding 5): what the backend actually used for each config-backed knob (placeholders).
  const resolvedConfig = resolvedConfigValues(data?.meta?.inputs);
  // Phase 1 (finding 7): the ladder rung for each expiry, so a row chip can say "rolling off".
  const rungByExpiry = useMemo(() => {
    const m = new Map<string, { rollingOff?: boolean | null; status?: string | null }>();
    (data?.ladderPriority?.rungs || []).forEach((r) => m.set(String(r.expiry), r));
    return m;
  }, [data?.ladderPriority?.rungs]);
  const filterAudit: FilterAudit | null = data?.filterAudit || null;
  // The entry-rule values the BACKEND actually resolved on the last run, read back out
  // of meta.inputs. CIO decision 2026-09-10 (audit Step 1): the filter inputs on this
  // page no longer carry hard-coded defaults that override the config file, so the CIO
  // needs to be able to see what is in force without opening entry_signals.json. These
  // feed the input placeholders below; a blank box means "using the value shown".
  const resolvedEntryConfig = useMemo(() => {
    const inputs = data?.meta?.inputs || {};
    const caps = (inputs.oi_caps || {}) as Record<string, any>;
    const capPct = (key: string): string | null => {
      const v = firstNumber(caps[key]);
      return v == null ? null : String(v);
    };
    return {
      minDte: firstNumber(inputs.min_dte),
      minOpenInterest: firstNumber(filterAudit?.minOpenInterest, inputs.min_open_interest),
      minOiForSizing: firstNumber(caps.min_oi_for_sizing),
      capUnder100: capPct("under_100"),
      cap100to499: capPct("100_to_499"),
      cap500to1499: capPct("500_to_1499"),
      cap1500plus: capPct("1500_or_more"),
    };
  }, [data?.meta?.inputs, filterAudit?.minOpenInterest]);
  const configHint = (value: number | string | null, suffix = ""): string =>
    value == null ? "from backend config" : `config: ${value}${suffix}`;
  const chainAudit: ChainAudit | null = data?.chainAudit || null;
  const quoteHydration = chainAudit?.quoteHydration || chainAudit?.market_data?.quote_hydration || null;
  const quoteHydrationSummary = useMemo(() => {
    const resolved = Number(chainAudit?.resolvedContracts ?? quoteHydration?.totalRequested ?? 0);
    const rowsReturned = Number(chainAudit?.snapshotRowsReturned ?? chainAudit?.marketDataAttached ?? quoteHydration?.snapshotRowsReturned ?? 0);
    const quoteComplete = Number(chainAudit?.quoteComplete ?? quoteHydration?.quoteComplete ?? 0);
    const greekComplete = Number(chainAudit?.greekComplete ?? quoteHydration?.greekComplete ?? 0);
    const fullComplete = Number(chainAudit?.quoteAndGreekComplete ?? quoteHydration?.quoteAndGreekComplete ?? 0);
    const incomplete = Math.max(0, resolved - fullComplete);
    const pct = resolved > 0 ? fullComplete / resolved : null;
    return {
      resolved,
      rowsReturned,
      quoteComplete,
      greekComplete,
      fullComplete,
      incomplete,
      pct,
      mode: String(quoteHydration?.mode || chainAudit?.market_data?.mode || "—"),
      elapsedMs: Number(quoteHydration?.elapsedMs ?? 0),
      singletonAttempted: Number(quoteHydration?.singletonAttempted ?? 0),
      statusCounts: quoteHydration?.statusCounts || {},
    };
  }, [chainAudit, quoteHydration]);
  const quoteHydrationTone: Tone = useMemo(() => {
    const explicitStatus = String(quoteHydration?.status || chainAudit?.quoteHydrationStatus || "").toLowerCase();
    if (["pass", "ok", "success", "complete", "completed", "hydrated"].includes(explicitStatus)) {
      return quoteHydrationSummary.incomplete > 0 ? "warning" : "success";
    }
    if (["warn", "warning", "partial", "incomplete", "retry", "needs_retry"].includes(explicitStatus)) {
      return "warning";
    }
    if (["fail", "failed", "error", "halt"].includes(explicitStatus)) {
      return "danger";
    }
    if (quoteHydrationSummary.resolved <= 0) return "neutral";
    if (quoteHydrationSummary.fullComplete >= quoteHydrationSummary.resolved) return "success";
    if (quoteHydrationSummary.fullComplete > 0) return "warning";
    return "danger";
  }, [
    chainAudit?.quoteHydrationStatus,
    quoteHydration?.status,
    quoteHydrationSummary.fullComplete,
    quoteHydrationSummary.incomplete,
    quoteHydrationSummary.resolved,
  ]);
  const auditWaterfall = filterAudit?.waterfall || [];
  const auditExpiryCoverage = useMemo(() => {
    const rows = [...(filterAudit?.expiryCoverage || [])];
    rows.sort((a, b) => String(a.expiry || "").localeCompare(String(b.expiry || "")));
    return rows;
  }, [filterAudit?.expiryCoverage]);
  const farthestExpiryCoverage = useMemo(() => auditExpiryCoverage.slice(-12).reverse(), [auditExpiryCoverage]);
  const chainExpiryPlan = useMemo(() => {
    const rows = [...(chainAudit?.expiryPlan || [])];
    rows.sort((a, b) => String(a.expiry || "").localeCompare(String(b.expiry || "")));
    return rows.slice(-12).reverse();
  }, [chainAudit?.expiryPlan]);
  const surfaceSummary = data?.meta?.surface_summary;
  // Phase 1 (P0, finding 1): the backend writes the shadow at meta.diagnostics.tenor_valuation_shadow; reading
  // meta.tenor_valuation_shadow meant the own-tenor warning NEVER rendered. Diagnostics first, meta as fallback.
  const tenorShadow = readTenorShadow(data?.meta);

  /* REMOVED 2026-08-07: avgSurfaceScore / avgVega / avgIv component-level useMemos.
     Compiler-verified dead (ESLint no-unused-vars) — three reducers re-running over
     every purchase on each data change, read by nothing. The same-named locals inside
     localUniverseGrade and the row-level row.avgVega / row.avgSurfaceScore in the
     ladder are DIFFERENT scopes and are untouched. */

  const tenorMixRows = useMemo(() => {
    const rankedRows: Purchase[] = (data?.rankedContracts?.length
      ? data.rankedContracts
      : data?.purchases || []
    ).filter((row: Purchase) => String(row.quoteStatus || "QUOTE_OK").toUpperCase() === "QUOTE_OK");
    if (!rankedRows.length) return [];

    const grouped = rankedRows.reduce((acc: Record<string, number>, p: Purchase) => {
      const bucket = p.tenorBucket || "unbucketed";
      acc[bucket] = (acc[bucket] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const total = rankedRows.length;
    return Object.entries(grouped as Record<string, number>)
      .map(([bucket, rawCount]) => {
        const count = Number(rawCount) || 0;
        return {
          bucket,
          label: titleizeBucket(bucket),
          count,
          pct: total > 0 ? count / total : 0,
          tone: toneFromTenorBucket(bucket),
        };
      })
      .sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0));
  }, [data?.rankedContracts, data?.purchases]);

  const maxTenorPct = useMemo(() => {
    if (!tenorMixRows.length) return 0;
    return Math.max(...tenorMixRows.map((row) => row.pct || 0));
  }, [tenorMixRows]);

  const moneynessBehavior = useMemo(() => {
    const baseBand =
      firstNumber(
        diagnostics?.selected_moneyness_band,
        diagnostics?.applied_band_pct,
        body.moneyness_band_pct
      ) ?? 0.05;

    const rowsForMoneyness = data?.rankedContracts?.length
      ? data.rankedContracts
      : data?.purchases || [];
    const totalCount = rowsForMoneyness.length;

    const bands = [
      { label: "Within 5%", upper: 0.05, count: 0, pct: 0 },
      { label: "5–6%", upper: 0.06, count: 0, pct: 0 },
      { label: "6–7%", upper: 0.07, count: 0, pct: 0 },
      { label: ">7%", upper: null as number | null, count: 0, pct: 0 },
    ];

    rowsForMoneyness.forEach((p) => {
      const absM = Math.abs(Number(p.moneyness || 0));
      if (absM <= 0.05) {
        bands[0].count += 1;
      } else if (absM <= 0.06) {
        bands[1].count += 1;
      } else if (absM <= 0.07) {
        bands[2].count += 1;
      } else {
        bands[3].count += 1;
      }
    });

    bands.forEach((row) => {
      row.pct = totalCount > 0 ? row.count / totalCount : 0;
    });

    const expansionActive = rowsForMoneyness.some((p) => Math.abs(Number(p.moneyness || 0)) > baseBand + 1e-9);
    const widestUsed = rowsForMoneyness.length
      ? Math.max(...rowsForMoneyness.map((p) => Math.abs(Number(p.moneyness || 0))))
      : 0;

    return {
      baseBand,
      bands,
      expansionActive,
      widestUsed,
    };
  }, [
    data?.rankedContracts,
    data?.purchases,
    diagnostics?.selected_moneyness_band,
    diagnostics?.applied_band_pct,
    body.moneyness_band_pct,
  ]);

  const ladderByExpiry = useMemo(() => {
    type LadderExpiryRow = {
      expiry: string;
      lines: number;
      contracts: number;
      deployedNotional: number;
      addedVega: number;
      avgVega: number | null;
      avgEntryScore: number | null;
      avgSurfaceScore: number | null;
      earliestDte: number | null;
      latestDte: number | null;
      tenorMixLabel: string;
      primaryTenorBucket: string | null;
      lineConids: string[];
    };

    const sourceRows = data?.rankedContracts?.length
      ? data.rankedContracts
      : data?.purchases || [];

    const backendRows = data?.rankedLadderByExpiration?.length
      ? data.rankedLadderByExpiration
      : data?.universeByExpiration?.length
      ? data.universeByExpiration
      : data?.meta?.diagnostics?.ranked_ladder_by_expiration || [];

    if (backendRows.length) {
      return backendRows.map((row): LadderExpiryRow => {
        const expiry = String(row.expiry || "Unknown");
        const expirySourceRows = sourceRows.filter((p) => String(p.expiry || "Unknown") === expiry);
        const tenorEntries = topCountEntries(row.tenorCounts || null, 6);
        const primaryTenor = tenorEntries.length ? tenorEntries[0][0] : null;
        const vegaVals = expirySourceRows
          .map((p) => p.vega)
          .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
        const avgVega = firstNumber(row.avgVega, row.avg_vega, row.averageVega) ?? (vegaVals.length ? vegaVals.reduce((a, b) => a + b, 0) / vegaVals.length : null);
        const sourceConids = expirySourceRows.map((contract) => String(contract.conid || "")).filter(Boolean);
        return {
          expiry,
          lines: Number(row.lines ?? row.rankedContracts ?? sourceConids.length ?? 0),
          contracts: Number(row.rankedContracts ?? row.lines ?? sourceConids.length ?? 0),
          deployedNotional: 0,
          addedVega: 0,
          avgVega,
          avgEntryScore: typeof row.avgEntryScore === "number" ? row.avgEntryScore : null,
          avgSurfaceScore: typeof row.avgSurfaceScore === "number" ? row.avgSurfaceScore : null,
          earliestDte: typeof row.minDte === "number" ? row.minDte : null,
          latestDte: typeof row.maxDte === "number" ? row.maxDte : null,
          tenorMixLabel: tenorEntries.length
            ? tenorEntries.map(([bucket, count]) => `${titleizeBucket(bucket)} (${count})`).join(" · ")
            : "—",
          primaryTenorBucket: primaryTenor,
          lineConids: (row.topContracts || []).map((contract) => String(contract.conid || "")).filter(Boolean).concat(sourceConids),
        };
      }).sort((a, b) => {
        const da = a.earliestDte ?? Number.POSITIVE_INFINITY;
        const db = b.earliestDte ?? Number.POSITIVE_INFINITY;
        if (da !== db) return da - db;
        return a.expiry.localeCompare(b.expiry);
      });
    }

    if (!sourceRows.length) return [] as LadderExpiryRow[];

    const grouped = new Map<string, Purchase[]>();
    sourceRows.forEach((p) => {
      const key = p.expiry || "Unknown";
      const arr = grouped.get(key) || [];
      arr.push(p);
      grouped.set(key, arr);
    });

    const rows = Array.from(grouped.entries()).map(([expiry, rows]) => {
      const uniqueConids = Array.from(new Set(rows.map((p) => String(p.conid))));
      const tenorCounts = rows.reduce<Record<string, number>>((acc, p) => {
        const key = p.tenorBucket || "unbucketed";
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      const tenorMix = Object.entries(tenorCounts).sort((a, b) => b[1] - a[1]);
      const entryScores = rows
        .map((p) => p.entryScore)
        .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
      const surfaceScores = rows
        .map((p) => p.surfaceScore)
        .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
      const dtes = rows
        .map((p) => p.dte)
        .filter((v): v is number => typeof v === "number" && Number.isFinite(v));

      return {
        expiry,
        lines: uniqueConids.length,
        contracts: marketRankOnly ? uniqueConids.length : rows.reduce((acc, p) => acc + (p.allocatedQty || 0), 0),
        deployedNotional: rows.reduce((acc, p) => acc + (p.estNotional || 0), 0),
        addedVega: rows.reduce((acc, p) => acc + (p.estVegaUSDperPt || 0), 0),
        avgVega: rows
          .map((p) => p.vega)
          .filter((v): v is number => typeof v === "number" && Number.isFinite(v)).length
          ? rows
              .map((p) => p.vega)
              .filter((v): v is number => typeof v === "number" && Number.isFinite(v))
              .reduce((a, b) => a + b, 0) /
            rows
              .map((p) => p.vega)
              .filter((v): v is number => typeof v === "number" && Number.isFinite(v)).length
          : null,
        avgEntryScore: entryScores.length
          ? entryScores.reduce((a, b) => a + b, 0) / entryScores.length
          : null,
        avgSurfaceScore: surfaceScores.length
          ? surfaceScores.reduce((a, b) => a + b, 0) / surfaceScores.length
          : null,
        earliestDte: dtes.length ? Math.min(...dtes) : null,
        latestDte: dtes.length ? Math.max(...dtes) : null,
        tenorMixLabel: tenorMix.length
          ? tenorMix.map(([bucket, count]) => `${titleizeBucket(bucket)} (${count})`).join(" · ")
          : "—",
        primaryTenorBucket: tenorMix.length ? tenorMix[0][0] : null,
        lineConids: uniqueConids,
      };
    });

    rows.sort((a, b) => {
      const da = a.earliestDte ?? Number.POSITIVE_INFINITY;
      const db = b.earliestDte ?? Number.POSITIVE_INFINITY;
      if (da !== db) return da - db;
      return a.expiry.localeCompare(b.expiry);
    });

    return rows;
  }, [data?.rankedLadderByExpiration, data?.universeByExpiration, data?.meta?.diagnostics?.ranked_ladder_by_expiration, data?.rankedContracts, data?.purchases, marketRankOnly]);

  const allCandidateRows = useMemo(() => {
    const universe = data?.rankedContracts?.length
      ? data.rankedContracts
      : data?.purchases || [];
    return universe;
  }, [data?.rankedContracts, data?.purchases]);

  // Phase 1 (P0, finding 1): the shadow in plain English, its DTE examples mapped back to expiries via the rows.
  const shadowRead = useMemo(() => describeTenorShadow(tenorShadow, allCandidateRows), [tenorShadow, allCandidateRows]);

  const postOiUniverseRows = useMemo(() => {
    const universe = data?.postOiUniverse?.length
      ? data.postOiUniverse
      : [];
    return [...universe].sort((a, b) => {
      const ar = Number(a.provisionalUniverseRank ?? a.postOiUniverseRank ?? 999999);
      const br = Number(b.provisionalUniverseRank ?? b.postOiUniverseRank ?? 999999);
      if (ar !== br) return ar - br;
      const ae = String(a.expiry || "");
      const be = String(b.expiry || "");
      if (ae !== be) return ae.localeCompare(be);
      return Number(a.strike || 0) - Number(b.strike || 0);
    });
  }, [data?.postOiUniverse]);

  const postOiUniverseSummary = useMemo(() => {
    const quoteOk = postOiUniverseRows.filter((row) => String(row.quoteStatus || "QUOTE_OK").toUpperCase() === "QUOTE_OK").length;
    const needsQuote = Math.max(0, postOiUniverseRows.length - quoteOk);
    const farthest = postOiUniverseRows.length
      ? postOiUniverseRows.reduce((max, row) => String(row.expiry || "") > String(max || "") ? String(row.expiry || "") : max, "")
      : "";
    return {
      total: postOiUniverseRows.length,
      quoteOk,
      needsQuote,
      farthest,
    };
  }, [postOiUniverseRows]);

  const candidateUniverseSummary = useMemo(() => {
    const selected = allCandidateRows.filter(isSelectedCandidate).length;
    const ranked = allCandidateRows.filter(isRankedCandidate).length;
    const skipped = allCandidateRows.filter((row) => String(row.allocationStatus || "").toUpperCase() === "SKIPPED").length;
    return {
      total: allCandidateRows.length,
      selected,
      ranked,
      skipped,
    };
  }, [allCandidateRows]);

  // mlTelemetryRows removed 2026-08-11 — it existed only to feed runEdgeMix and
  // deploymentInterpretation, both of which went with the Deployment Interpretation card.

  const universeGrade = useMemo<UniverseGrade>(() => {
    const backendGrade =
      data?.universeGrade ||
      data?.overallGrade ||
      data?.meta?.universe_grade ||
      data?.meta?.diagnostics?.universe_grade ||
      null;
    if (backendGrade?.grade) return backendGrade;
    return localUniverseGrade(allCandidateRows);
  }, [allCandidateRows, data?.meta?.diagnostics?.universe_grade, data?.meta?.universe_grade, data?.overallGrade, data?.universeGrade]);

  const contractQualityGrade = useMemo<UniverseGrade>(() => {
    const backendGrade =
      data?.contractQualityGrade ||
      data?.meta?.contract_quality_grade ||
      decisionContext?.contractQuality ||
      universeGrade ||
      null;
    if (backendGrade?.grade) return backendGrade;
    return universeGrade;
  }, [data?.contractQualityGrade, data?.meta?.contract_quality_grade, decisionContext?.contractQuality, universeGrade]);

  const priorityRankAvailable = useMemo(
    () => allCandidateRows.some((p) => typeof p.priorityRank === "number"),
    [allCandidateRows]
  );

  const sortedPurchases = useMemo(() => {
    if (!allCandidateRows.length) return [];
    const arr = [...allCandidateRows];
    const dir = sortDir === "asc" ? 1 : -1;

    arr.sort((a, b) => {
      const va = getCellValue(a, sortKey);
      const vb = getCellValue(b, sortKey);
      const aU = va == null;
      const bU = vb == null;
      if (aU && bU) return 0;
      if (aU) return 1;
      if (bU) return -1;

      if (typeof va === "number" && typeof vb === "number") {
        if (va === vb) return 0;
        return va < vb ? -1 * dir : 1 * dir;
      }

      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      if (sa === sb) return 0;
      return sa < sb ? -1 * dir : 1 * dir;
    });

    return arr;
  }, [allCandidateRows, sortKey, sortDir]);

  const guardrailRows = useMemo(() => {
    const rows: Array<{ label: string; tone: Tone; status: string; detail: string }> = [];

    if (marketRankOnly) {
      const rankedRows = allCandidateRows.filter((row) => isRankedCandidate(row) || row.fullScoreEligible === true);
      const rankedCount = rankedRows.length || candidateUniverseSummary.ranked || candidateUniverseSummary.total;
      const postOiCount = postOiUniverseSummary.total || rankedCount;
      // Phase 1 (finding 5): the backend publishes ladder_min_distinct_expiries (never min_distinct_expiries), so
      // this used to fall back to the page's own hard-coded 3 against config 4. Unknown is said, not defaulted.
      const minExpiryTarget = resolveMinDistinctExpiries(data?.meta?.inputs);
      const expiryCount = ladderByExpiry.length || (rankedRows.length ? new Set(rankedRows.map((p) => p.expiry || "Unknown")).size : 0);
      const widest = moneynessBehavior.widestUsed;

      rows.push({
        label: "Universe reconciliation",
        tone: postOiCount === rankedCount && postOiUniverseSummary.needsQuote === 0 ? "success" : "warning",
        status: postOiCount === rankedCount && postOiUniverseSummary.needsQuote === 0 ? "Tied out" : "Review",
        detail: `${numberFmt(postOiCount, 0)} contracts passed DTE, moneyness, and OI; ${numberFmt(rankedCount, 0)} are full-score ranked${postOiUniverseSummary.needsQuote ? ` and ${numberFmt(postOiUniverseSummary.needsQuote, 0)} still need quote review.` : "."}`,
      });

      // "Quote hydration" row removed 2026-10-28 (Phase 2): quote hydration is shown ONCE, in the diagnostics drawer.

      rows.push(
        minExpiryTarget == null
          ? {
              label: "Ranked expiry breadth",
              tone: "neutral",
              status: "Target not reported",
              detail: `${numberFmt(expiryCount, 0)} ranked expiries are visible; this run did not report its ladder breadth target (ladder_min_distinct_expiries).`,
            }
          : {
              label: "Ranked expiry breadth",
              tone: expiryCount >= minExpiryTarget ? "success" : "warning",
              status: expiryCount >= minExpiryTarget ? "Covered" : "Thin",
              detail: `${numberFmt(expiryCount, 0)} ranked expiries are visible against the ${numberFmt(minExpiryTarget, 0)}-expiry ladder objective. This is breadth visibility, not an allocation instruction.`,
            }
      );

      rows.push({
        label: "Moneyness filter",
        tone: moneynessBehavior.expansionActive ? "warning" : "success",
        status: moneynessBehavior.expansionActive ? "Edge buffer used" : "Anchored",
        detail: `${numberFmt(rankedCount, 0)} ranked contracts remain within the active moneyness policy; widest absolute moneyness is ${pctFmt(widest, 1)} versus base ${pctFmt(moneynessBehavior.baseBand, 0)}.`,
      });

      return rows;
    }

    return rows;
  }, [
    marketRankOnly,
    allCandidateRows,
    candidateUniverseSummary.ranked,
    candidateUniverseSummary.total,
    postOiUniverseSummary.total,
    postOiUniverseSummary.needsQuote,
    ladderByExpiry.length,
    moneynessBehavior,
    data?.meta?.inputs,
  ]);

  







  const stagedTicketSummary = useMemo(() => {
      const states: TicketState[] = Object.values(ticketStateByConid) as TicketState[];
      return {
        total: states.length,
        queued: states.filter(
          (s: TicketState) => s.status === "queued" || s.status === "duplicate"
        ).length,
        newlyStaged: states.filter((s: TicketState) => s.status === "staged").length,
      };
    }, [ticketStateByConid]);

  /**
   * Ticked rows that are actually ready to queue — sized, priced, not already staged.
   * A row ticked but still missing a quantity is intentionally excluded so the button
   * count never promises more than it will do.
   */
  const readyToQueue = useMemo(
    () => allCandidateRows.filter(
      (p) => selectedForQueue.has(String(p.conid)) && queueBlocker(p) == null
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allCandidateRows, selectedForQueue, ticketStateByConid, cioQtyByConid, lotRule, lotExceptionByConid, lotRefusedByConid]
  );

  const selectedQueueCount = readyToQueue.length;

  /**
   * The running deployment budget: what this session has queued, plus what is ticked and
   * sized, against the amount typed in the sizing box.
   *
   * The basket counts every TICKED and sized row, including one still waiting on a lot-minimum
   * exception reason. That row is intent — the CIO is building toward it — and leaving it out
   * would understate the basket at the moment he is deciding whether he has room for it.
   */
  const budgetTally = useMemo(
    () =>
      computeScreenerBudget({
        budgetText: capital,
        queued: Object.values(sessionQueuedByConid),
        basket: allCandidateRows
          .filter((p) => selectedForQueue.has(String(p.conid)) && !ticketStateByConid[String(p.conid)])
          .map((p) => {
            const s = resolveTicketSizing(p);
            return { qty: s.qty, notional: s.notional };
          }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [capital, sessionQueuedByConid, allCandidateRows, selectedForQueue, ticketStateByConid, cioQtyByConid]
  );

  const exportToExcel = () => {
    if (!data) return;
    const diagnostics = data.meta?.diagnostics || {};
    const risk = riskContext || ({} as ScreenerRiskContext);
    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <style>
            body { font-family: Arial, sans-serif; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 16px; }
            th, td { border: 1px solid #cbd5e1; padding: 6px 8px; font-size: 12px; }
            th { background: #e2e8f0; text-align: left; }
            h2 { margin: 12px 0 6px; }
          </style>
        </head>
        <body>
          <h2>Screener Summary</h2>
          <table>
            <tr><th>Symbol</th><td>${escHtml(data.meta.symbol)}</td><th>Spot</th><td>${escHtml(numberFmt(data.meta.spot, 2))}</td></tr>
            <tr><th>Run as of (UTC)</th><td>${escHtml(reportedOr(data.meta.asOf))}</td><th>Order</th><td>${escHtml(sortKey === "priorityRank" ? priorityToggleLabel(data.ladderPriority) : "Entry Score")} (Contracts sheet below is in the on-screen order)</td></tr>
            <tr><th>This Run Used</th><td colspan="3">${escHtml(runUsed.line || "risk context not reported")}</td></tr>
            ${marketRankOnly ? `<tr><th>Allocation</th><td colspan="3">NOT COMPUTED — this run was market-rank-only, so the allocator did not run. Total Notional, Budget Left, Added Vega and every per-row Qty below are not results.</td></tr>` : ""}
            <tr><th>Total Notional</th><td>${escHtml(marketRankOnly ? "—" : currency(data.totals.total_notional))}</td><th>Added Vega</th><td>${escHtml(marketRankOnly ? "—" : numberFmt(data.totals.total_added_vega_usd_per_pt, 1))}</td></tr>
            <tr><th>Budget Left</th><td>${escHtml(marketRankOnly ? "—" : currency(data.totals.budget_left))}</td><th>Avg Entry Score</th><td>${escHtml(numberFmt(avgEntryScore, 3))}</td></tr>
            <tr><th>Contract Quality</th><td>${escHtml(gradeWithTenorNote(contractQualityGrade.grade))}</td><th>Contract Score</th><td>${escHtml(numberFmt(contractQualityGrade.score, 3))}</td></tr>
            <tr><th>Allocation Validation</th><td>${escHtml(String(allocationValidation?.status || "not_returned"))}</td><th>Invariant Violations</th><td>${escHtml(String(allocationValidationViolations.length))}</td></tr>
            <tr><th>Contract Quality Read</th><td colspan="3">${escHtml(`${contractQualityGrade.label || ""}${contractQualityGrade.summary ? ` — ${contractQualityGrade.summary}` : ""}`)}</td></tr>
            <tr><th>Decision Context</th><td>${escHtml(String(decisionContext?.status || "not_returned"))}</td><th>Data Confidence</th><td>${escHtml(String(dataConfidence?.status || decisionContext?.dataConfidence?.status || "not_returned"))}</td></tr>
            <tr><th>CIO Action</th><td colspan="3">${escHtml(decisionContext?.cioActionLabel || "Review")}</td></tr>
            <tr><th>Decision Message</th><td colspan="3">${escHtml(decisionContext?.primaryMessage || "Backend decision context was not returned.")}</td></tr>
            <tr><th>Market Opportunity</th><td>${escHtml(decisionContext?.marketOpportunity?.label || "")}</td><th>Portfolio Permission</th><td>${escHtml(decisionContext?.portfolioPermission?.label || "")}</td></tr>
            <tr><th>Allocator Result</th><td colspan="3">${escHtml(titleizeCode(decisionContext?.allocatorResult?.code))}</td></tr>
            <tr><th>Validation Detail</th><td colspan="3">${escHtml(trustHardBlocker || allocationValidationBlocker || "No hard data or allocation invariant blocker returned.")}</td></tr>
            <tr>
                <th>Market Best Bucket (surface)</th>
                <td>${escHtml(reportedOr(risk.bestDeploymentBucket))}</td>
                <th>Preferred Bucket Notional</th>
                <td>${escHtml(marketRankOnly ? "— (allocator did not run)" : currency(sweetSpotNotional))}</td>
              </tr>
              <tr>
                <th>Active Allocation Bucket</th>
                <td>${escHtml(marketRankOnly ? "— (allocator did not run)" : reportedOr(activeAllocationBucket))}</td>
                <th>Filter Pass</th>
                <td>${escHtml(reportedOr(diagnostics?.chosen_filter_pass))}</td>
              </tr>
            <tr><th>Applied Band</th><td colspan="3">${escHtml(reportedOr(diagnostics?.applied_band_pct))}</td></tr>
          </table>
          <h2>Chain Audit</h2>
          <table>
            <tr><th>Expiry Scope</th><td>${escHtml(chainAudit?.explicitAllExpiries ? "All DTE-qualified expiries" : "Capped")}</td><th>Months Limit</th><td>${escHtml(String(chainAudit?.monthsLimit ?? data.meta.inputs?.months_limit ?? ""))}</td></tr>
            <tr><th>Expiries Total / Used</th><td>${escHtml(`${chainAudit?.expiriesTotal ?? "—"} / ${chainAudit?.expiriesUsed ?? "—"}`)}</td><th>Farthest Used</th><td>${escHtml((chainAudit?.expiriesUsedList || []).slice(-1)[0] || "—")}</td></tr>
            <tr><th>Expected / Resolved / Snapshot Rows</th><td colspan="3">${escHtml(`${chainAudit?.expectedSpecs ?? "—"} / ${chainAudit?.resolvedContracts ?? "—"} / ${chainAudit?.marketDataAttached ?? "—"}`)}</td></tr>
            <tr><th>Quote Hydration</th><td colspan="3">${escHtml(`${chainAudit?.quoteFullScoreComplete ?? quoteHydration?.fullScoreComplete ?? "—"} / ${chainAudit?.resolvedContracts ?? quoteHydration?.requestedConids ?? "—"}; incomplete ${chainAudit?.quoteIncomplete ?? quoteHydration?.quoteIncomplete ?? "—"}; attempts ${(quoteHydration?.attempts || []).length || "—"}`)}</td></tr>
          </table>
          <h2>Filter Waterfall</h2>
          <table>
            <thead><tr><th>Stage</th><th>Count</th><th>Share of Raw</th><th>Dropped From Prior</th><th>Drop Reason</th></tr></thead>
            <tbody>
              ${(filterAudit?.waterfall || []).map((row) => `
                <tr><td>${escHtml(row.label || row.stage || "")}</td><td>${escHtml(numberFmt(row.count ?? null, 0))}</td><td>${escHtml(pctFmt(row.shareOfRaw ?? null, 1))}</td><td>${escHtml(row.droppedFromPrior == null ? "—" : numberFmt(row.droppedFromPrior, 0))}</td><td>${escHtml(titleizeCode(row.dropReason))}</td></tr>`).join("")}
            </tbody>
          </table>
          <h2>Expiry Coverage</h2>
          <table>
            <thead><tr><th>Expiry</th><th>Raw</th><th>Moneyness</th><th>OI</th><th>Mid</th><th>Vega</th><th>Qualified</th><th>Top Blockers</th></tr></thead>
            <tbody>
              ${(filterAudit?.expiryCoverage || []).map((row) => `
                <tr><td>${escHtml(row.expiry || "")}</td><td>${escHtml(numberFmt(row.raw ?? null, 0))}</td><td>${escHtml(numberFmt(row.moneyness ?? null, 0))}</td><td>${escHtml(numberFmt(row.oi ?? null, 0))}</td><td>${escHtml(numberFmt(row.mid ?? null, 0))}</td><td>${escHtml(numberFmt(row.vega ?? null, 0))}</td><td>${escHtml(numberFmt(row.qualified ?? null, 0))}</td><td>${escHtml(topCountText(row.dropCounts || null, 3))}</td></tr>`).join("")}
            </tbody>
          </table>
          <h2>Ladder by Expiration</h2>
          <table>
            <thead>
              <tr>
                <th>Expiry</th><th>Lines</th><th>Contracts</th><th>${marketRankOnly ? "Ranked Contracts" : "Deployed Notional"}</th><th>${marketRankOnly ? "Avg Contract Vega" : "Added Vega"}</th><th>Avg Entry Score</th><th>Avg Surface Score</th><th>DTE Range</th><th>Tenor Mix</th>
              </tr>
            </thead>
            <tbody>
              ${ladderByExpiry.map((row) => `
                <tr>
                  <td>${escHtml(row.expiry)}</td>
                  <td>${escHtml(String(row.lines))}</td>
                  <td>${escHtml(String(row.contracts))}</td>
                  <td>${escHtml(marketRankOnly ? numberFmt(row.contracts, 0) : currency(row.deployedNotional))}</td>
                  <td>${escHtml(marketRankOnly ? numberFmt(row.avgVega, 1) : numberFmt(row.addedVega, 1))}</td>
                  <td>${escHtml(numberFmt(row.avgEntryScore, 3))}</td>
                  <td>${escHtml(numberFmt(row.avgSurfaceScore, 3))}</td>
                  <td>${escHtml(row.earliestDte != null && row.latestDte != null ? `${row.earliestDte}–${row.latestDte}` : "—")}</td>
                  <td>${escHtml(row.tenorMixLabel)}</td>
                </tr>`).join("")}
            </tbody>
          </table>
          <h2>Full Universe After Moneyness + OI</h2>
          ${htmlTable(FULL_UNIVERSE_HEADERS, postOiUniverseRows.map((p) => fullUniverseCells(p)))}
          <h2>Contracts (in the on-screen ${escHtml(sortKey === "priorityRank" ? "Priority" : "sorted")} order)</h2>
          ${htmlTable(
            CONTRACTS_HEADERS,
            sortedPurchases.map((p) =>
              contractsCells(p, {
                decision: isRankedCandidate(p) ? "RANKED" : isSelectedCandidate(p) ? "BUY" : "SKIPPED",
                reason: isRankedCandidate(p)
                  ? p.whyIncluded || p.decisionReason || "Ranked market screen"
                  : isSelectedCandidate(p)
                  ? p.selectionReason || "Selected allocation"
                  : titleizeSkipReason(p.skipReason),
                marketRankOnly,
                rollingOff: rungByExpiry.get(String(p.expiry))?.rollingOff ?? null,
              })
            )
          )}
        </body>
      </html>`;
    downloadExcelWorkbook(`Helion_Screener_${data.meta.symbol}_${new Date().toISOString().slice(0,10)}.xls`, html);
  };

  const avgEntryScore = useMemo(() => {
    if (!allCandidateRows.length) return null;
    const vals = allCandidateRows.map((p) => p.entryScore).filter((v): v is number => typeof v === "number");
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [allCandidateRows]);


  // The long composed run-summary sentence was cut 2026-08-11: on the live path it was
  // never reached, because the backend builds decision_context unconditionally and every
  // branch sets a non-empty primaryMessage. What survives is a THIN fallback, kept
  // deliberately — the render that now carries this text used to degrade to the
  // placeholder "Backend has not returned decision-context detail for this run", which
  // is not a summary of anything. Rotation reason, budget left and deployment mode all
  // remain on the page in their own panels, so the fallback does not restate them.
  const decisionNarrative = useMemo(() => {
    if (!data) return null;
    if (decisionContext?.primaryMessage) return decisionContext.primaryMessage;
    const symbolLabel = data.meta.symbol || "SPX";
    const lineCount = candidateUniverseSummary.ranked || candidateUniverseSummary.total;
    const noun = "contracts ranked";
    return `${symbolLabel}: ${lineCount} ${noun} across ${ladderByExpiry.length} expiries in ${runUsed.mode || "an unreported"} mode. Backend decision context was not returned for this run.`;
  }, [data, decisionContext?.primaryMessage, ladderByExpiry.length, candidateUniverseSummary.ranked, candidateUniverseSummary.total, runUsed.mode]);


  // runEdgeMix and deploymentInterpretation removed 2026-08-11 with the Deployment
  // Interpretation card, their only consumer. deploymentInterpretation's decisionContext
  // branch just re-labelled cioActionLabel / primaryMessage, and its heuristic branch was
  // unreachable because the backend always returns decision_context. runEdgeMix computed
  // a structural/surface/ML contribution split whose ML share is now structurally 0.

  // ---------------------------------------------------------------------------------------------------------------
  // SCREENER PHASE 2 (layout, audit 2026-10-27; CIO approved "Answer first" + cut the allocator leftovers).
  // Run bar -> Next buy + ladder at cost -> Ranked contracts -> Context -> one Diagnostics drawer. Display only:
  // the request body, ranking, Priority order, ladder math, quantities, limit prices and the ticket path are
  // unchanged (pinned by ScreenView.phase2.test.tsx).
  // ---------------------------------------------------------------------------------------------------------------

  // "N strikes qualify" on each rung tile: the rows the ranked table shows, per expiry.
  const strikeCounts = useMemo(() => strikesByExpiry(allCandidateRows), [allCandidateRows]);

  const dataConfidenceStatus = String(dataConfidence?.status || decisionContext?.dataConfidence?.status || "").toLowerCase();
  const contextErrorList = [contextErrors.snapshot, contextErrors.kpis, contextErrors.surface, contextErrors.vega].filter(
    (x): x is string => Boolean(x)
  );
  // ONE status for every audit / diagnostic block, and the drawer opens itself on any non-pass state.
  const diagIssues = useMemo(
    () =>
      data
        ? diagnosticsIssues({
            dataConfidenceStatus,
            decisionContextFailed,
            hardBlockCount: hardDecisionBlocks.length,
            invariantViolations: allocationValidationViolations.length,
            quotesResolved: quoteHydrationSummary.resolved,
            quotesIncomplete: quoteHydrationSummary.incomplete,
            quoteTone: quoteHydrationTone,
            integrityRows: marketRankOnly ? guardrailRows : [],
            riskContextFailed: riskContextStatus === "failed",
            contextErrors: contextErrorList,
            runArchiveStatus: data.runArchive?.status ?? null,
            pendingReadFailed: Boolean(pendingReadFailed),
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, dataConfidenceStatus, decisionContextFailed, hardDecisionBlocks.length, allocationValidationViolations.length, quoteHydrationSummary, quoteHydrationTone, marketRankOnly, guardrailRows, riskContextStatus, contextErrorList.join("|"), pendingReadFailed]
  );
  const diagHeadline = diagnosticsHeadline(diagIssues);
  const diagOpen = diagUserOpen ?? diagIssues.length > 0;
  // A new run starts from its own state again: a drawer the CIO closed yesterday must not hide today's warning.
  useEffect(() => {
    setDiagUserOpen(null);
  }, [data?.meta?.asOf]);

  const unknownIfBlank = (v: string | null | undefined) => (v == null || v === "" || v === "—" ? "Unknown" : v);
  const navAsOfLine = riskContextStatus === "loading" ? "loading…" : navAsOfPart(riskContext?.navAsOf);
  const vixAsOfLine = riskContextStatus === "loading" ? "loading…" : vixAsOfPart(riskContext?.vixAsOf);
  const runAsOfLine = data?.meta?.asOf ? new Date(data.meta.asOf).toLocaleString() : "not reported";
  const dataChip: { text: string; tone: ChipTone } =
    dataConfidenceStatus === "pass" || dataConfidenceStatus === "ok"
      ? { text: "Data ✓", tone: "positive" }
      : dataConfidenceStatus === "warn" || dataConfidenceStatus === "warning"
      ? { text: "Data: WARN", tone: "warning" }
      : ["fail", "halt", "error"].includes(dataConfidenceStatus)
      ? { text: "Data: FAIL", tone: "danger" }
      : { text: "Data: not reported", tone: "warning" };
  const quotesChip: { text: string; tone: ChipTone } =
    quoteHydrationSummary.resolved > 0
      ? {
          text: `Quotes ${numberFmt(quoteHydrationSummary.fullComplete, 0)}/${numberFmt(quoteHydrationSummary.resolved, 0)}`,
          tone: quoteHydrationTone === "success" ? "positive" : quoteHydrationTone === "danger" ? "danger" : "warning",
        }
      : { text: "Quotes: not reported", tone: "warning" };

  // The Filters panel, rendered by the run bar when open (Screener Phase 3: its own component).
  const filtersPanel = (
    <ScreenerFilters
      bandPct={bandPct}
      budgetTally={budgetTally}
      canSubmit={canSubmit}
      capital={capital}
      configHint={configHint}
      currentVega={currentVega}
      earliestMonthsAhead={earliestMonthsAhead}
      expirationCapPct={expirationCapPct}
      harvestLockout={harvestLockout}
      minDistinctExpiries={minDistinctExpiries}
      minDte={minDte}
      minOI={minOI}
      moneynessBufferPct={moneynessBufferPct}
      monthsLimit={monthsLimit}
      oiCap100to499={oiCap100to499}
      oiCap1500plus={oiCap1500plus}
      oiCap500to1499={oiCap500to1499}
      oiCapUnder100={oiCapUnder100}
      resolvedConfig={resolvedConfig}
      resolvedEntryConfig={resolvedEntryConfig}
      right={right}
      runScreener={runScreener}
      setBandPct={setBandPct}
      setCapital={setCapital}
      setCurrentVega={setCurrentVega}
      setEarliestMonthsAhead={setEarliestMonthsAhead}
      setExpirationCapPct={setExpirationCapPct}
      setMinDistinctExpiries={setMinDistinctExpiries}
      setMinDte={setMinDte}
      setMinOI={setMinOI}
      setMoneynessBufferPct={setMoneynessBufferPct}
      setMonthsLimit={setMonthsLimit}
      setOiCap100to499={setOiCap100to499}
      setOiCap1500plus={setOiCap1500plus}
      setOiCap500to1499={setOiCap500to1499}
      setOiCapUnder100={setOiCapUnder100}
      setRight={setRight}
      setSymbol={setSymbol}
      symbol={symbol}
    />
  );

  return (
    <Layout>
      <div
        className="mx-auto w-full px-2 py-6 sm:px-4 xl:px-6"
        style={{ backgroundColor: ui.pageBg, color: ui.textPrimary }}
      >
        <h1 className="mb-4 text-2xl font-semibold">Options Screener (LEAPS-first)</h1>

        {/* ============================== 1. RUN BAR ============================== */}
        <RunBar
          canSubmit={canSubmit}
          data={data}
          dataChip={dataChip}
          dataConfidence={dataConfidence}
          error={error}
          exportToExcel={exportToExcel}
          filtersPanel={filtersPanel}
          loading={loading}
          mlWeight={mlWeight}
          quotesChip={quotesChip}
          requester={requester}
          riskContextStatus={riskContextStatus}
          runAsOfLine={runAsOfLine}
          runScreener={runScreener}
          runUsed={runUsed}
          setData={setData}
          setShowFilters={setShowFilters}
          showFilters={showFilters}
        />

        {data && (
          <>
            {/* ============================== 2. NEXT BUY + LADDER AT COST ============================== */}
            <NextBuySection
              data={data}
              shadowRead={shadowRead}
              strikeCounts={strikeCounts}
            />

            {/* ============================== 3. RANKED CONTRACTS ============================== */}
            <RankedContractsTable
              allCandidateRows={allCandidateRows}
              budgetTally={budgetTally}
              candidateUniverseSummary={candidateUniverseSummary}
              cioQtyByConid={cioQtyByConid}
              data={data}
              expandedContractKey={expandedContractKey}
              lotExceptionByConid={lotExceptionByConid}
              lotPreviewFor={lotPreviewFor}
              lotRule={lotRule}
              marketRankOnly={marketRankOnly}
              pendingReadFailed={pendingReadFailed}
              priorityRankAvailable={priorityRankAvailable}
              queueBlocker={queueBlocker}
              resolveTicketSizing={resolveTicketSizing}
              rungByExpiry={rungByExpiry}
              selectedForQueue={selectedForQueue}
              selectedQueueCount={selectedQueueCount}
              setCioQtyByConid={setCioQtyByConid}
              setExpandedContractKey={setExpandedContractKey}
              setLotExceptionByConid={setLotExceptionByConid}
              setLotRefusedByConid={setLotRefusedByConid}
              setSelectedForQueue={setSelectedForQueue}
              setShowAnalyticsColumns={setShowAnalyticsColumns}
              setSortDir={setSortDir}
              setSortKey={setSortKey}
              showAnalyticsColumns={showAnalyticsColumns}
                  sortedPurchases={sortedPurchases}
              sortDir={sortDir}
          sortKey={sortKey}
              stageBanner={stageBanner}
              stagedTicketSummary={stagedTicketSummary}
              stageSelectedPurchases={stageSelectedPurchases}
              stageSinglePurchase={stageSinglePurchase}
              stagingAll={stagingAll}
              stagingConid={stagingConid}
          toggleSort={toggleSort}
                          ticketStateByConid={ticketStateByConid}
              trustHardBlock={trustHardBlock}
              trustHardBlocker={trustHardBlocker}
            />
          </>
        )}

        {/* ============================== 4. CONTEXT (one card, deduped, every figure dated) ============================== */}
        <ContextCard
          contextErrors={contextErrors}
          data={data}
          decisionContext={decisionContext}
          decisionNarrative={decisionNarrative}
          defenseDisplay={defenseDisplay}
          harvestFetchedAtMs={harvestFetchedAtMs}
          harvestLockout={harvestLockout}
          navAsOfLine={navAsOfLine}
          portfolioVega={portfolioVega}
          riskContext={riskContext}
          riskContextStatus={riskContextStatus}
          runAsOfLine={runAsOfLine}
          surfaceSummary={surfaceSummary}
          tilesMeta={tilesMeta}
          unknownIfBlank={unknownIfBlank}
          vixAsOfLine={vixAsOfLine}
        />

        {/* ============================== 5. DIAGNOSTICS (one drawer) ============================== */}
        {data && (
          <DiagnosticsDrawer
            allCandidateRows={allCandidateRows}
            allocationValidation={allocationValidation}
            allocationValidationViolations={allocationValidationViolations}
            auditWaterfall={auditWaterfall}
            candidateUniverseSummary={candidateUniverseSummary}
            chainAudit={chainAudit}
            chainExpiryPlan={chainExpiryPlan}
            contractQualityGrade={contractQualityGrade}
            data={data}
            dataConfidence={dataConfidence}
            decisionContext={decisionContext}
            diagHeadline={diagHeadline}
            diagOpen={diagOpen}
            farthestExpiryCoverage={farthestExpiryCoverage}
            filterAudit={filterAudit}
            guardrailRows={guardrailRows}
            marketRankOnly={marketRankOnly}
            maxTenorPct={maxTenorPct}
            mlWeight={mlWeight}
            moneynessBehavior={moneynessBehavior}
            postOiUniverseRows={postOiUniverseRows}
            postOiUniverseSummary={postOiUniverseSummary}
            quoteHydration={quoteHydration}
            quoteHydrationSummary={quoteHydrationSummary}
            quoteHydrationTone={quoteHydrationTone}
            setDiagUserOpen={setDiagUserOpen}
            surfaceSummary={surfaceSummary}
            tenorMixRows={tenorMixRows}
          />
        )}

        <style>{`
          input:focus, select:focus {
            box-shadow: 0 0 0 3px ${ui.focusRing};
          }
        `}</style>
      </div>
    </Layout>
  );
}
